import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { useToast } from '../components/Toast';
import {
  ArrowLeft,
  Camera,
  X,
  Loader2,
  MapPin,
  Fuel,
} from 'lucide-react';

const FUEL_TYPES = [
  { value: 'GASOLINA', label: 'Gasolina' },
  { value: 'ETANOL', label: 'Etanol' },
  { value: 'DIESEL', label: 'Diesel' },
] as const;

const fuelSchema = z.object({
  km_current: z.number({ error: 'Informe a quilometragem' }).positive('Deve ser maior que 0'),
  liters: z.number({ error: 'Informe a litragem' }).positive('Deve ser maior que 0'),
  total_amount: z.number({ error: 'Informe o valor' }).positive('Deve ser maior que 0'),
  station_name: z.string().optional(),
});

type FuelFormData = z.infer<typeof fuelSchema>;

function getCurrentPosition(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      reject,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  });
}

export default function FuelForm() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fuelType, setFuelType] = useState<string>('GASOLINA');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FuelFormData>({
    resolver: zodResolver(fuelSchema),
  });

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handlePhotoCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const uploadPhoto = async (file: File): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    return new Promise((resolve, reject) => {
      img.onload = () => {
        const maxWidth = 1280;
        const scale = Math.min(1, maxWidth / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(img.src);

        canvas.toBlob(
          async (blob) => {
            if (!blob) return reject(new Error('Falha ao comprimir imagem'));
            const formData = new FormData();
            formData.append('photo', blob, `fuel_${Date.now()}.jpg`);

            try {
              const res = await api.post('/photos/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              resolve(res.data.data.url);
            } catch (err) {
              reject(err);
            }
          },
          'image/jpeg',
          0.7
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const submitMutation = useMutation({
    mutationFn: async (data: FuelFormData) => {
      if (!photoFile) throw new Error('Tire uma foto do cupom');

      // Get GPS
      let gps: { latitude: number; longitude: number } | null = null;
      try {
        gps = await getCurrentPosition();
      } catch {
        // GPS optional
      }

      // Upload photo
      const photo_url = await uploadPhoto(photoFile);

      const payload: Record<string, unknown> = {
        km_current: data.km_current,
        liters: data.liters,
        total_amount: data.total_amount,
        fuel_type: fuelType,
        station_name: data.station_name || undefined,
        photo_url,
      };
      if (gps) {
        payload.latitude = gps.latitude;
        payload.longitude = gps.longitude;
      }

      const res = await api.post('/fuel', payload);
      return res.data.data;
    },
    onSuccess: () => {
      toast({ title: 'Abastecimento registrado com sucesso', variant: 'success' });
      setLocation('/');
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error?.message || error.message || 'Erro ao registrar abastecimento';
      toast({ title: msg, variant: 'error' });
    },
  });

  const onSubmit = (data: FuelFormData) => {
    if (!photoFile) {
      toast({ title: 'Tire uma foto do cupom', variant: 'warning' });
      return;
    }
    submitMutation.mutate(data);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation('/')}
            className="p-2 -ml-2 rounded-full active:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-bold">Registrar Abastecimento</h1>
        </div>
      </header>

      <main className="flex-1 p-4 space-y-4">
        {/* Fuel Type Toggle */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipo de Combustivel *
          </label>
          <div className="grid grid-cols-3 gap-2">
            {FUEL_TYPES.map((ft) => (
              <button
                key={ft.value}
                type="button"
                onClick={() => setFuelType(ft.value)}
                className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-colors ${
                  fuelType === ft.value
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 active:bg-gray-200'
                }`}
              >
                {ft.label}
              </button>
            ))}
          </div>
        </div>

        {/* KM Atual */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <label htmlFor="km_current" className="block text-sm font-medium text-gray-700 mb-1">
            KM Atual *
          </label>
          <input
            id="km_current"
            type="number"
            inputMode="numeric"
            {...register('km_current', { valueAsNumber: true })}
            className={`w-full px-3 py-3 rounded-xl border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
              errors.km_current ? 'border-red-300' : 'border-gray-200'
            }`}
            placeholder="Ex: 45230"
          />
          {errors.km_current && (
            <p className="text-xs text-red-500 mt-1">{errors.km_current.message}</p>
          )}
        </div>

        {/* Liters + Total Amount in 2 columns */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <label htmlFor="liters" className="block text-sm font-medium text-gray-700 mb-1">
              Litros *
            </label>
            <input
              id="liters"
              type="number"
              inputMode="decimal"
              step="0.01"
              {...register('liters', { valueAsNumber: true })}
              className={`w-full px-3 py-3 rounded-xl border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
                errors.liters ? 'border-red-300' : 'border-gray-200'
              }`}
              placeholder="0,00"
            />
            {errors.liters && (
              <p className="text-xs text-red-500 mt-1">{errors.liters.message}</p>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <label htmlFor="total_amount" className="block text-sm font-medium text-gray-700 mb-1">
              Valor Total (R$) *
            </label>
            <input
              id="total_amount"
              type="number"
              inputMode="decimal"
              step="0.01"
              {...register('total_amount', { valueAsNumber: true })}
              className={`w-full px-3 py-3 rounded-xl border text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
                errors.total_amount ? 'border-red-300' : 'border-gray-200'
              }`}
              placeholder="0,00"
            />
            {errors.total_amount && (
              <p className="text-xs text-red-500 mt-1">{errors.total_amount.message}</p>
            )}
          </div>
        </div>

        {/* Station Name */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <label htmlFor="station_name" className="block text-sm font-medium text-gray-700 mb-1">
            Nome do Posto (opcional)
          </label>
          <input
            id="station_name"
            type="text"
            {...register('station_name')}
            className="w-full px-3 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
            placeholder="Ex: Posto Shell Centro"
          />
        </div>

        {/* Photo */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Foto do Cupom *
          </label>
          {photoPreview ? (
            <div className="relative">
              <img
                src={photoPreview}
                alt="Cupom"
                className="w-full h-40 object-cover rounded-xl border border-gray-200"
              />
              <button
                type="button"
                onClick={() => {
                  setPhotoPreview(null);
                  setPhotoFile(null);
                }}
                className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-full text-white"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 active:bg-gray-50"
            >
              <Camera className="h-8 w-8" />
              <span className="text-sm font-medium">Tirar foto do cupom</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoCapture}
            className="hidden"
          />
        </div>

        {/* GPS info */}
        <div className="flex items-center gap-2 px-2 text-xs text-gray-400">
          <MapPin className="h-3.5 w-3.5" />
          <span>Localizacao sera capturada automaticamente</span>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit(onSubmit)}
          disabled={submitMutation.isPending}
          className="w-full flex items-center justify-center gap-2 p-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitMutation.isPending ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Fuel className="h-5 w-5" />
              Registrar Abastecimento
            </>
          )}
        </button>
      </main>
    </div>
  );
}
